import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Mic, MicOff, Keyboard, CheckCircle, XCircle, Search as SearchIcon } from 'lucide-react';
import useOnlineStatus from '../hooks/useOnlineStatus';
import useSpeechRecognition from '../hooks/useSpeechRecognition';

const VoiceCommandContext = createContext();

export const VoiceCommandProvider = ({ children }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [typedCommand, setTypedCommand] = useState('');
  const [recognition, setRecognition] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isOnline = useOnlineStatus();
  const { isSupported: isSpeechSupported, error: speechError } = useSpeechRecognition();
  const navigate = useNavigate();

  // Initialize speech recognition
  useEffect(() => {
    if (!isSpeechSupported || speechError) {
      console.warn('Speech recognition not supported or error:', speechError);
      return;
    }

    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';

        // Optimizations for faster response
        recognitionInstance.maxAlternatives = 1;  // Only get the top alternative
        recognitionInstance.interimResults = false; // Don't wait for final results

        recognitionInstance.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0])
            .map(result => result.transcript)
            .join('');
          setTranscript(transcript);
          
          // Process command immediately without waiting for final result
          processCommand(transcript);
        };

        recognitionInstance.onerror = (event) => {
          console.error('Speech recognition error', event.error);
          showFeedback(`Error: ${event.error}`, true);
          setIsListening(false);
        };

        recognitionInstance.onend = () => {
          if (isListening) {
            recognitionInstance.start();
          }
        };

        setRecognition(recognitionInstance);
      }
    }

    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, [isListening, isSpeechSupported, speechError]);

  // Keyboard shortcut (Ctrl + K or Cmd + K) for command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        toggleCommandPalette();
      } else if (e.key === 'Escape') {
        setIsListening(false);
        setShowCommandPalette(false);
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'm' && !showCommandPalette) {
        e.preventDefault();
        toggleListening();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCommandPalette]);

  const toggleListening = useCallback(() => {
    if (!isSpeechSupported) {
      showFeedback(speechError || 'Speech recognition not supported in your browser', true);
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
      setTranscript('');
      showFeedback('Voice commands paused');
    } else {
      try {
        recognition.start();
        setIsListening(true);
        showFeedback('Listening...');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        showFeedback('Error starting voice recognition. Please check microphone permissions.', true);
      }
    }
  }, [isListening, recognition, isSpeechSupported, speechError]);

  // Process voice or typed commands
  const processCommand = useCallback((command) => {
    if (!command || isProcessing) return;
    
    setIsProcessing(true);
    const normalizedCommand = command.toLowerCase().trim();
    
    try {
      // Navigation commands
      if (matchesCommand(normalizedCommand, ['go to dashboard', 'open dashboard', 'dashboard'])) {
        navigate('/dashboard');
        showFeedback('Navigating to dashboard');
      } 
      else if (matchesCommand(normalizedCommand, ['go to members', 'show members', 'view members'])) {
        navigate('/members');
        showFeedback('Showing members');
      }
      else if (matchesCommand(normalizedCommand, ['go to reports', 'show reports', 'view reports'])) {
        navigate('/reports');
        showFeedback('Showing reports');
      }
      else if (matchesCommand(normalizedCommand, ['add new member', 'create member', 'new member'])) {
        // This would trigger the new member form
        showFeedback('Opening new member form');
        // You can add logic to open a form or navigate to a specific page
      }
      else if (matchesCommand(normalizedCommand, ['stop listening', 'turn off microphone', 'exit voice control'])) {
        setIsListening(false);
        showFeedback('Voice commands paused');
      }
      else if (matchesCommand(normalizedCommand, ['help', 'what can i say', 'show commands'])) {
        showHelp();
      }
      else if (matchesCommand(normalizedCommand, ['go to settings', 'open settings'])) {
        navigate('/settings');
        showFeedback('Opening settings');
      }
      else if (matchesCommand(normalizedCommand, ['go to profile', 'my profile'])) {
        navigate('/profile');
        showFeedback('Opening your profile');
      }
      else if (matchesCommand(normalizedCommand, ['go back', 'navigate back'])) {
        navigate(-1);
        showFeedback('Going back');
      }
      else if (matchesCommand(normalizedCommand, ['go forward', 'navigate forward'])) {
        navigate(1);
        showFeedback('Going forward');
      }
      else if (matchesCommand(normalizedCommand, ['go home', 'main menu', 'home page'])) {
        navigate('/');
        showFeedback('Going to home page');
      }
      else if (matchesCommand(normalizedCommand, ['scroll down', 'scroll down the page', 'page down'])) {
        window.scrollBy({ 
          top: window.innerHeight * 0.8,
          behavior: 'smooth' 
        });
        showFeedback('Scrolling down');
      }
      else if (matchesCommand(normalizedCommand, ['scroll up', 'scroll up the page', 'page up'])) {
        window.scrollBy({ 
          top: -window.innerHeight * 0.8,
          behavior: 'smooth' 
        });
        showFeedback('Scrolling up');
      }
      else if (matchesCommand(normalizedCommand, ['scroll to bottom', 'scroll down to the bottom', 'go to bottom'])) {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
        showFeedback('Scrolling to bottom');
      }
      else if (matchesCommand(normalizedCommand, ['scroll to top', 'scroll up to the top', 'go to top'])) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        showFeedback('Scrolling to top');
      }
      else if (matchesCommand(normalizedCommand, ['go to login', 'login page', 'show login'])) {
        navigate('/login');
        showFeedback('Navigating to login page');
      }
      else if (matchesCommand(normalizedCommand, ['login', 'sign in', 'click login'])) {
        // Check if we're on the login page
        if (window.location.pathname === '/login') {
          // Try different selectors to find the login button
          const selectors = [
            'button[type="submit"]',
            '.login-button',
            'button:contains("Login")',
            'button:contains("Sign In")',
            'button',
            'input[type="submit"]',
            'button.ant-btn-primary',
            'button.ant-btn' // Add more selectors as needed
          ];

          let loginButton = null;
          for (const selector of selectors) {
            loginButton = document.querySelector(selector);
            if (loginButton) {
              console.log('Found login button with selector:', selector);
              break;
            }
          }

          if (loginButton) {
            try {
              loginButton.click();
              showFeedback('Logging in...');
              return; // Exit early if successful
            } catch (error) {
              console.error('Error clicking login button:', error);
              showFeedback('Error clicking login button', true);
              return;
            }
          }
          showFeedback('Login button not found', true);
          console.log('Login button not found. Available buttons:', document.querySelectorAll('button, input[type="submit"]'));
        } else {
          navigate('/login');
        }
      }
      // Admin Dashboard Commands
      else if (matchesCommand(normalizedCommand, ['go to dashboard', 'show dashboard', 'admin dashboard'])) {
        navigate('/admindashboard');
        showFeedback('Navigating to admin dashboard');
      }
      else if (matchesCommand(normalizedCommand, ['show members', 'view members', 'all members'])) {
        navigate('/adminmembers');
        showFeedback('Showing all members');
      }
      else if (matchesCommand(normalizedCommand, ['show opds', 'view opds', 'all opds'])) {
        navigate('/adminopds');
        showFeedback('Showing all OPDs');
      }
      else if (matchesCommand(normalizedCommand, ['show reports', 'view reports', 'open reports'])) {
        navigate('/reports');
        showFeedback('Opening reports');
      }
      else if (matchesCommand(normalizedCommand, ['show messages', 'view messages', 'admin messages'])) {
        navigate('/adminmessages');
        showFeedback('Opening messages');
      }
      else if (matchesCommand(normalizedCommand, ['support tracking', 'view support', 'show support'])) {
        navigate('/support');
        showFeedback('Opening support tracking');
      }
      else if (matchesCommand(normalizedCommand, ['opd support', 'show opd support', 'view opd support'])) {
        navigate('/supportperopd');
        showFeedback('Opening OPD support tracking');
      }
      else if (matchesCommand(normalizedCommand, ['settings', 'admin settings', 'open settings'])) {
        navigate('/adminsettings');
        showFeedback('Opening admin settings');
      }
      else if (matchesCommand(normalizedCommand, ['logout', 'sign out', 'log out'])) {
        // Find and click the logout button
        const logoutButton = document.querySelector('button:contains("Logout"), button:has(svg[data-icon="log-out"])');
        if (logoutButton) {
          logoutButton.click();
          showFeedback('Logging out...');
        } else {
          navigate('/login');
        }
      }
      // If no command is matched
      else {
        showFeedback(`Command not recognized: "${command}"`, true);
      }
    } catch (error) {
      console.error('Error processing command:', error);
      showFeedback('Error processing command. Please try again.', true);
    } finally {
      setIsProcessing(false);
      setTranscript('');
      setIsListening(false);
    }
  }, [navigate, isProcessing]);

  // Helper function to check if command matches any of the given phrases
  const matchesCommand = (command, phrases) => {
    return phrases.some(phrase => command.includes(phrase));
  };

  const showFeedback = (message, isError = false) => {
    if (isError) {
      toast.error(message, { 
        icon: <XCircle className="text-red-500" />,
        style: {
          background: '#FEE2E2',
          color: '#B91C1C',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }
      });
    } else {
      toast.success(message, { 
        icon: <CheckCircle className="text-green-500" />,
        style: {
          background: '#D1FAE5',
          color: '#047857',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }
      });
    }
  };

  const showHelp = () => {
    const commands = [
      { command: 'Go to dashboard', description: 'Navigate to the dashboard' },
      { command: 'Show members', description: 'View all members' },
      { command: 'Show reports', description: 'View reports' },
      { command: 'Add new member', description: 'Open the new member form' },
      { command: 'Go to settings', description: 'Open application settings' },
      { command: 'Go to profile', description: 'View your profile' },
      { command: 'Go back', description: 'Navigate back' },
      { command: 'Go home', description: 'Return to the home page' },
      { command: 'Stop listening', description: 'Turn off voice commands' },
      { command: 'Help', description: 'Show available commands' },
      { command: 'Scroll down', description: 'Scroll down the page' },
      { command: 'Scroll up', description: 'Scroll up the page' },
      { command: 'Scroll to bottom', description: 'Scroll to the bottom of the page' },
      { command: 'Scroll to top', description: 'Scroll to the top of the page' },
      { command: 'Go to login', description: 'Navigate to login page' },
      { command: 'Login', description: 'Click the login button' },
      { command: 'Show dashboard', description: 'Go to admin dashboard' },
      { command: 'Show members', description: 'View all members' },
      { command: 'Show OPDs', description: 'View all OPDs' },
      { command: 'Show reports', description: 'View reports' },
      { command: 'Show messages', description: 'View admin messages' },
      { command: 'Support tracking', description: 'View support tracking' },
      { command: 'OPD support', description: 'View OPD support tracking' },
      { command: 'Settings', description: 'Open admin settings' },
      { command: 'Logout', description: 'Sign out of the admin panel' },
    ];

    toast.custom((t) => (
      <div className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Available Voice Commands</h3>
          <button 
            onClick={() => toast.dismiss(t.id)}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close help"
          >
            ✕
          </button>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
          {commands.map((cmd, index) => (
            <div key={index} className="flex group">
              <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm text-gray-800 flex-1 group-hover:bg-gray-200 transition-colors">
                {cmd.command}
              </span>
              <span className="ml-2 text-gray-600 text-sm self-center">
                {cmd.description}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-gray-500">
          <p>Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">M</kbd> to toggle voice commands</p>
          <p>Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">K</kbd> to open command palette</p>
        </div>
      </div>
    ), {
      duration: 15000, // Show for 15 seconds
      position: 'top-center',
      style: {
        maxWidth: '90vw',
        width: '100%',
      },
    });
  };

  const toggleCommandPalette = useCallback(() => {
    setShowCommandPalette(prev => !prev);
    if (!showCommandPalette) {
      setTypedCommand('');
    }
  }, [showCommandPalette]);

  const handleTypedCommand = useCallback((e) => {
    if (e.key === 'Enter' && typedCommand.trim()) {
      processCommand(typedCommand);
      setTypedCommand('');
      setShowCommandPalette(false);
    }
  }, [typedCommand, processCommand]);

  return (
    <VoiceCommandContext.Provider
      value={{
        isListening,
        transcript,
        toggleListening,
        isOnline,
        showCommandPalette,
        toggleCommandPalette,
        typedCommand,
        setTypedCommand,
        handleTypedCommand,
        processCommand,
        isSpeechSupported,
        speechError
      }}
    >
      {children}
    </VoiceCommandContext.Provider>
  );
};

export const useVoiceCommand = () => {
  const context = useContext(VoiceCommandContext);
  if (!context) {
    throw new Error('useVoiceCommand must be used within a VoiceCommandProvider');
  }
  return context;
};
